import { useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getVisitorId, getDemoButtonVariant } from '../utils/abTest';
import { ADMIN_EMAILS } from '../pages/admin/adminApi';
import { trackMeta, communityParams } from '../utils/metaPixel';

// Anonymous visit analytics for community home pages — visits, time on page
// (only while the tab is visible), scroll depth, what was clicked, and how
// the visit ended. Feeds the admin "Page Analytics" tab. Everything is
// best-effort and never blocks or throws on the page.

const HEARTBEAT_MS = 15 * 1000;
const MAX_HEARTBEAT_MS = 30 * 60 * 1000;
const FIRST_SEND_DELAY_MS = 1500; // let Firebase auth settle so logged-in/admin flags are right
const MAX_ACTIONS = 60; // the quick-match flow alone can be 15–25 taps
// Not a bounce (backend/services/communityAnalyticsService.js): 10s+ on
// screen, scrolled 25%+, or any click. Sent to the Meta Pixel once per visit.
const ENGAGED_MS = 10 * 1000;
const ENGAGED_SCROLL = 25;

// The path the browser first loaded — document.referrer only describes how
// someone arrived at *that*, not at pages reached by in-app navigation later.
const LANDING_PATH = typeof window !== 'undefined' ? window.location.pathname : '';
let landingViewUsed = false;

// Site pages tracked with this same hook in place of a community id (and
// accepted by the backend: routes/communityAnalytics.js SITE_PAGES).
export const SITE_PAGE_IDS = ['page-create'];

const newId = () => ((crypto.randomUUID && crypto.randomUUID()) || `pv_${Date.now()}_${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');

export const getDevice = () => {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
  return 'desktop';
};

const getReferrer = () => {
  if (landingViewUsed || window.location.pathname !== LANDING_PATH) return '(in-site)';
  try {
    if (!document.referrer) return null;
    const host = new URL(document.referrer).hostname.replace(/^www\./, '');
    return host === window.location.hostname.replace(/^www\./, '') ? '(in-site)' : host;
  } catch {
    return null;
  }
};

export const isInternalVisit = () => {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  // Automated browsers (test scripts, speed/monitoring tools, ad-review bots)
  // set navigator.webdriver — not real visitors.
  if (navigator.webdriver === true) return true;
  const email = getAuth().currentUser?.email;
  return !!email && ADMIN_EMAILS.includes(email);
};

// How far down the page the visitor has seen (0-100). On mobile the site
// scrolls inside <body> (overflow: auto) rather than the window, so window
// scroll maths always read 100% there — measure whichever element actually
// scrolls. Same logic as the early tracker in scripts/generate-og-pages.js.
export const scrollPercent = () => {
  const els = [document.scrollingElement || document.documentElement, document.body].filter(Boolean);
  let best = null;
  for (const el of els) {
    const range = el.scrollHeight - el.clientHeight;
    if (!best || range > best.range) best = { el, range };
  }
  if (!best || best.range <= 0) return 100; // nothing to scroll: whole page is in view
  return Math.min(100, Math.round(((best.el.scrollTop + best.el.clientHeight) / best.el.scrollHeight) * 100));
};

// Work out what a click on the page meant, from the element's class / link.
const classifyClick = (el) => {
  const cls = typeof el.className === 'string' ? el.className : '';
  const href = el.getAttribute('href') || '';
  // Quick-match hero card and its pop-up: its own type, except the links /
  // buttons that lead to signing up, which still count as Join.
  if (el.closest && el.closest('.qm-card, .qm-modal')) {
    return el.dataset?.qmJoin || href.includes('/groupsearch/join/') ? 'join' : 'quickmatch';
  }
  // The cookie banner's Accept / Decline — recorded, but not a click on the page.
  if (el.closest && el.closest('.cookie-banner')) return 'cookies';
  if (/join-button/.test(cls) || href.includes('/groupsearch/join/')) return 'join';
  if (/^\/(privacy|terms|contact)\b/i.test(href)) return 'legal';
  if (/connect-button/.test(cls) || href.includes('action=connect')) return 'connect';
  if (/try-now-button/.test(cls) || href.startsWith('/demo') || /[?&]mode=demo\b/.test(href)) return 'demo';
  if (/watch-video-button|inline-cover_aboutVideo|close_aboutVideo/.test(cls)) return 'video';
  if (/scroll-cue/.test(cls)) return 'more';
  if (/create-account|signup|sign-up|\/login/i.test(href)) return 'signup';
  if (el.tagName === 'A' && href) {
    try {
      const url = new URL(href, window.location.href);
      return url.hostname === window.location.hostname ? 'internal_link' : 'external_link';
    } catch {
      return 'other_click';
    }
  }
  return 'other_click';
};

// heroFlow / heroFlowVariant: which hero they saw ('quickmatch' / 'default')
// and their A/B group, for communities using the quick-match hero.
// aboutVideo / aboutVideoVariant: for communities with a "Watch the video",
// whether this visit had the button ('shown' / 'hidden') and its A/B group.
const useCommunityPageAnalytics = (communityId, { isLoggedIn, isMember, heroFlow, heroFlowVariant, aboutVideo, aboutVideoVariant, communityTitle, communityRef }) => {
  // For the Meta Pixel: which community (one pixel serves them all).
  const metaCommunityRef = useRef({});
  metaCommunityRef.current = { title: communityTitle, ref: communityRef };
  const flagsRef = useRef({ isLoggedIn, isMember, heroFlow, heroFlowVariant, aboutVideo, aboutVideoVariant });
  flagsRef.current = { isLoggedIn, isMember, heroFlow, heroFlowVariant, aboutVideo, aboutVideoVariant };

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!communityId || !apiBase) return;

    const startedAt = Date.now();
    const seenKey = `ic_seen_community_${communityId}`;
    let isReturning = false;
    try { isReturning = !!localStorage.getItem(seenKey); } catch { /* storage blocked — treat as new */ }

    // Pre-built community pages start a tiny tracker in the HTML itself
    // (window.__IC_PV__) so visits are counted from the moment the page
    // appears — including visitors who leave before this app has loaded.
    // Take that visit over rather than starting a second one.
    const early = window.__IC_PV__;
    const adopt = !!early && !early.adopted && early.communityId === String(communityId);
    if (adopt) early.adopted = true;
    const isLandingView = !landingViewUsed && window.location.pathname === LANDING_PATH;

    const params = new URLSearchParams(window.location.search);
    const state = {
      pageViewId: adopt ? early.pageViewId : newId(),
      visitorId: getVisitorId(),
      communityId: String(communityId),
      path: window.location.pathname,
      referrer: adopt ? early.referrer : getReferrer(),
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
      device: getDevice(),
      isReturning,
      demoButtonVariant: getDemoButtonVariant(),
    };
    // Time on page counts from when the page first appeared, not from when
    // this app finished loading (seconds later on a slow phone): the early
    // tracker's visible time if adopted, else time since navigation start.
    let accumulatedMs = adopt
      ? early.activeMs()
      : (isLandingView && document.visibilityState === 'visible' ? Math.round(performance.now()) : 0);
    let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    let maxScroll = 0; // the early tracker doesn't measure scroll: before the app loads the page is only the hero

    // Load timings (ms from navigation start for the landing view, from this
    // view's start for in-app navigation): when the page first painted, and
    // when the hero photo finished loading. Lets the dashboard tell slow loads
    // apart from visitors who saw the page and weren't interested.
    const sinceStart = () => (isLandingView ? Math.round(performance.now()) : Date.now() - startedAt);
    let heroLoadedMs;
    const heroImg = () => document.querySelector('#root .hero-section_communityHome img');
    const already = heroImg();
    if (!adopt && already && already.complete && already.naturalWidth > 0) {
      const entry = isLandingView && performance.getEntriesByName(already.currentSrc || already.src)[0];
      heroLoadedMs = entry ? Math.round(entry.responseEnd) : sinceStart();
    }
    const onImgLoad = (e) => {
      if (heroLoadedMs === undefined && e.target?.tagName === 'IMG' && e.target.closest?.('.hero-section_communityHome')) heroLoadedMs = sinceStart();
    };
    const timings = () => {
      if (adopt) return { firstPaintMs: early.firstPaintMs?.(), heroLoadedMs: early.heroLoadedMs?.() ?? heroLoadedMs };
      let firstPaintMs;
      if (isLandingView) {
        const fcp = performance.getEntriesByName('first-contentful-paint')[0];
        if (fcp) firstPaintMs = Math.round(fcp.startTime);
      }
      return { firstPaintMs, heroLoadedMs };
    };
    const actions = [];
    let sentOnce = false;
    let exited = false;

    const activeMs = () => accumulatedMs + (visibleSince ? Date.now() - visibleSince : 0);

    // Meta Pixel EngagedVisit (consent-gated in utils/metaPixel.js).
    const metaParams = communityParams({ id: state.communityId, ...metaCommunityRef.current });
    let engagedSent = false;
    const checkEngaged = () => {
      if (engagedSent || SITE_PAGE_IDS.includes(state.communityId)) return; // not a community
      if (activeMs() >= ENGAGED_MS || maxScroll >= ENGAGED_SCROLL || actions.length > 0) {
        engagedSent = true;
        trackMeta('EngagedVisit', metaParams, { internal: isInternalVisit() });
      }
    };
    const engagedTimer = setInterval(() => {
      checkEngaged();
      if (engagedSent) clearInterval(engagedTimer);
    }, 1000);

    const send = (extra = {}) => {
      if (!sentOnce) {
        sentOnce = true;
        landingViewUsed = true;
        try { localStorage.setItem(seenKey, '1'); } catch { /* ignore */ }
      }
      const body = JSON.stringify({
        ...state,
        ...flagsRef.current,
        internal: isInternalVisit(),
        activeMs: activeMs(),
        maxScroll,
        ...timings(),
        actions,
        ...extra,
      });
      try {
        fetch(`${apiBase}/analytics/community-view`, {
          method: 'POST',
          keepalive: true, // survives the page being closed / navigated away
          headers: { 'Content-Type': 'text/plain' },
          body,
        }).catch(() => {});
      } catch { /* ignore */ }
    };

    const updateScroll = () => {
      const pct = scrollPercent();
      if (pct > maxScroll) maxScroll = pct;
      checkEngaged();
    };
    let scrollQueued = false;
    const onScroll = () => {
      if (scrollQueued) return;
      scrollQueued = true;
      requestAnimationFrame(() => { scrollQueued = false; updateScroll(); });
    };

    const onClick = (e) => {
      const el = e.target.closest && e.target.closest('a, button, [role="button"]');
      if (!el || actions.length >= MAX_ACTIONS) return;
      const type = classifyClick(el);
      actions.push({
        type,
        // Quick-match elements carry a clearer label in data-qm.
        label: (el.dataset?.qm || el.getAttribute('aria-label') || el.innerText || '').trim().slice(0, 60),
        target: el.getAttribute('href') || undefined,
        atMs: activeMs(),
      });
      checkEngaged();
      send();
    };

    // Things that aren't clicks (a dropdown choice, what a search found…)
    // reported by components as window 'ic:track' events.
    const onTrack = (e) => {
      const { type, label } = e.detail || {};
      if (!type || actions.length >= MAX_ACTIONS) return;
      actions.push({ type, label: String(label || '').slice(0, 60), atMs: activeMs() });
      checkEngaged();
      send();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (visibleSince) { accumulatedMs += Date.now() - visibleSince; visibleSince = null; }
        send(); // mobile browsers often never fire pagehide — flush now
      } else if (!visibleSince) {
        visibleSince = Date.now();
      }
    };

    const onPageHide = () => {
      if (exited) return;
      exited = true;
      send({ exitType: 'left' });
    };

    // An adopted visit should be recorded straight away: the early tracker only
    // sends on exit, and it stops sending once this hook has taken over.
    const firstSend = setTimeout(() => { updateScroll(); send(); }, adopt ? 0 : FIRST_SEND_DELAY_MS);
    const heartbeat = setInterval(() => {
      if (Date.now() - startedAt > MAX_HEARTBEAT_MS) return clearInterval(heartbeat);
      if (document.visibilityState === 'visible') send();
    }, HEARTBEAT_MS);

    // Capture on document: catches window scrolling and <body> scrolling (which
    // doesn't bubble to window), i.e. both desktop and mobile layouts.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('click', onClick, true);
    document.addEventListener('load', onImgLoad, true); // load doesn't bubble; capture sees it
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('ic:track', onTrack);

    return () => {
      clearTimeout(firstSend);
      clearInterval(heartbeat);
      clearInterval(engagedTimer);
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('load', onImgLoad, true);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('ic:track', onTrack);
      // React StrictMode mounts/unmounts instantly in dev — don't log that as a visit.
      if (!sentOnce && Date.now() - startedAt < 300) {
        if (adopt) early.adopted = false; // let the remount take the visit over again
        return;
      }
      if (!exited) {
        exited = true;
        // Cleanup runs after the router has changed the URL, so this is where they went.
        send({ exitType: 'navigated', nextPath: window.location.pathname + window.location.search });
      }
    };
  }, [communityId]);
};

export default useCommunityPageAnalytics;
